import React, { FC, useEffect, useState, useContext } from "react";
import {
  Text,
  Input,
  FilledButton as Button,
  theme,
} from "@ifgengineering/component-library";
import { Field, Formik, Form } from "formik";
import { BackContainer, Container, Link, OutlineButton } from "../styled";
import * as Yup from "yup";
import Icon from "@icon-park/react/es/all";
import { navigate } from "@reach/router";
import { AuthComponentsContext } from "../../../Context";

type FieldType = {
  field: {
    onBlur: () => void;
    onChange: () => void;
    value: string;
  };
  form: {
    errors: {
      email: string;
    };
    touched: {
      email: string;
    };
  };
};

export const Schema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
});

type CheckYourEmailProps = {
  contactURL: string;
  loginURL: string;
};

const CheckYourEmail: FC<CheckYourEmailProps> = ({ contactURL, loginURL }) => (
  <Container direction="column" gap={16}>
    <Text type="T24" color="SLATE800" fontFamily="archiasemibold">
      Check your email
    </Text>
    <Container direction="column" gap={32}>
      <Container direction="column" gap={16}>
        <Text type="P16" color="SLATE800">
          If your email is in our database, you will receive an email with a
          link to reset your password. Please check your junk folder as well, as
          the email may sometimes end up there.
        </Text>
        <Text type="P16" color="SLATE800">
          Should the issue persist, please check the email inputted or{" "}
          <Link href={contactURL} target="_blank" fontSize="16px">
            contact our support.
          </Link>
        </Text>
      </Container>
      <Button
        type="button"
        text="Login"
        onClick={() => navigate(loginURL)}
        color="blue"
        height="48px"
        borderRadius="8px"
      />
    </Container>
  </Container>
);

interface ForgotPasswordProps {
  loginURL?: string;
  contactURL?: string;
  showFormSubmitted?: boolean;
  onSubmit?: (email: string) => void | Promise<void>;
}

const ForgotPassword: FC<ForgotPasswordProps> = ({
  loginURL = "/auth/signin",
  contactURL = "https://www.islamicfinanceguru.com/contact-us",
  onSubmit,
  showFormSubmitted,
}) => {
  const { useAnalytics, sendResetPasswordLink } = useContext(
    AuthComponentsContext
  );
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLinkSent, setIsLinkSent] = useState(false);
  const fireForgotPasswordAnalytics = useAnalytics(
    "forogtPasswordSubmitEmailClick"
  );

  useEffect(() => {
    if (showFormSubmitted !== undefined) {
      setIsLinkSent(showFormSubmitted);
    }
  }, [showFormSubmitted]);

  const handleSubmit = async (values: { email: string }) => {
    if (isLoading) return; // Prevent double submission

    const email = values.email.trim().toLocaleLowerCase();
    setIsLoading(true);

    if (onSubmit) {
      try {
        (await fireForgotPasswordAnalytics)({
          email,
        });
        await onSubmit(email);
      } finally {
        setIsLoading(false);
      }
      return;
    } else {
      try {
        await sendResetPasswordLink(email);
        setIsLinkSent(true);
      } catch (e: any) {
        if (e.response.status === 404) {
          setError("We couldn't find this email in our database.");
        }
        console.error(e);
      }
      setIsLoading(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={Schema}
      initialValues={{ email: "" }}
      onSubmit={handleSubmit}
    >
      <Form>
        <Container direction="column" padding="24px">
          {isLinkSent ? (
            <CheckYourEmail contactURL={contactURL} loginURL={loginURL} />
          ) : (
            <Container direction="column" gap={16}>
              <Text type="T24" color="SLATE800" fontFamily="archiasemibold">
                {"Let's get your account back!"}
              </Text>
              <Container direction="column" gap={32}>
                <Field name="email">
                  {({ field, form: { errors, touched } }: FieldType) => (
                    <Input
                      {...field}
                      errorMessage={
                        (touched.email ? errors.email : undefined) || error
                      }
                      testId="email-input"
                      heading="Email"
                    />
                  )}
                </Field>
                <Container direction="row" gap={16}>
                  <OutlineButton onClick={() => navigate(loginURL)}>
                    Back
                  </OutlineButton>
                  <Button
                    testId="submit-email-button"
                    type="submit"
                    height="48px"
                    text="Submit"
                    color="blue"
                    borderRadius="8px"
                    isLoading={isLoading}
                  />
                </Container>
              </Container>
            </Container>
          )}
        </Container>
      </Form>
    </Formik>
  );
};

export default ForgotPassword;
